import { addDays, addMonths, fromKey, isDayKey, monthCells, toKey } from '../home/agenda/agendaModel';

/**
 * P-52 (D-225 sec.1 / QX-440) - pure helpers of the shared date picker
 * (DatePickerLayer): the month grid, the small Hebrew date under each Gregorian
 * day (Intl `he-u-ca-hebrew`, no library), disabled days from min/max.
 */

export { addDays, addMonths, fromKey, isDayKey, toKey };

const HE_PARTS = new Intl.DateTimeFormat('he-u-ca-hebrew', { day: 'numeric', month: 'long', year: 'numeric' });

const ONES = ['', 'א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ז', 'ח', 'ט'];
const TENS = ['', 'י', 'כ', 'ל', 'מ', 'נ', 'ס', 'ע', 'פ', 'צ'];
const HUNDREDS = ['', 'ק', 'ר', 'ש', 'ת'];

/** P-55c (D-230 sec.6): a number as Hebrew letters (gematria) - 15 = ט״ו, 16 = ט״ז, 23 = כ״ג, 787 = תשפ״ז; one letter gets a geresh (א׳). */
export function hebrewLetters(n: number): string {
  let rest = Math.max(0, Math.floor(n));
  let out = '';
  while (rest >= 400) { out += 'ת'; rest -= 400; }
  if (rest >= 100) { out += HUNDREDS[Math.floor(rest / 100)]; rest %= 100; }
  if (rest === 15) out += 'טו';
  else if (rest === 16) out += 'טז';
  else { out += TENS[Math.floor(rest / 10)] + ONES[rest % 10]; }
  if (out.length <= 1) return `${out}׳`;
  return `${out.slice(0, -1)}״${out.slice(-1)}`;
}

/** The Hebrew year in letters without the thousands: 5787 -> תשפ״ז. */
export function hebrewYearLetters(year: number): string {
  return hebrewLetters(year % 1000);
}

interface HebrewParts { day: number; month: string; year: number }

function hebrewParts(d: Date): HebrewParts {
  const parts = HE_PARTS.formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return { day: Number(get('day')), month: get('month'), year: Number(get('year')) };
}

/** The Hebrew day of the month for a key in letters (`2026-10-21` -> "י׳", the 1st also carries the month name: "א׳ חשוון"). */
export function hebrewDay(key: string): string {
  const p = hebrewParts(fromKey(key));
  const letters = hebrewLetters(p.day);
  return p.day === 1 ? `${letters} ${p.month}` : letters;
}

/** A full Hebrew date in letters: "י׳ חשוון תשפ״ז". */
export function hebrewDateLabel(key: string): string {
  const p = hebrewParts(fromKey(key));
  return `${hebrewLetters(p.day)} ${p.month} ${hebrewYearLetters(p.year)}`;
}

/** "חשוון תשפ״ז" or, when the Gregorian month spans two Hebrew months, "תשרי - חשוון תשפ״ז". */
export function hebrewMonthLabel(anyDayInMonth: string): string {
  const cells = monthCells(anyDayInMonth).filter((c): c is string => !!c);
  const a = hebrewParts(fromKey(cells[0]));
  const b = hebrewParts(fromKey(cells[cells.length - 1]));
  const yb = hebrewYearLetters(b.year);
  if (a.month === b.month && a.year === b.year) return `${b.month} ${yb}`;
  return a.year === b.year ? `${a.month} - ${b.month} ${yb}` : `${a.month} ${hebrewYearLetters(a.year)} - ${b.month} ${yb}`;
}

/** dd/mm/yyyy for a day key ('' for anything else) - the display format of every date field (D-230 sec.7). */
export function formatDmy(key: string | null | undefined): string {
  if (!key || !isDayKey(key)) return '';
  return `${key.slice(8, 10)}/${key.slice(5, 7)}/${key.slice(0, 4)}`;
}

export function gregorianMonthLabel(anyDayInMonth: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(fromKey(anyDayInMonth));
}

/** Seven short weekday names, Sunday first. */
export function weekdayLabels(locale: string): string[] {
  const f = new Intl.DateTimeFormat(locale, { weekday: 'short' });
  // 2026-10-04 is a Sunday.
  return Array.from({ length: 7 }, (_, i) => f.format(fromKey(addDays('2026-10-04', i))));
}

/** min/max are the input's own attributes: anything that is not a day key is ignored. */
export function isDayDisabled(key: string, min?: string | null, max?: string | null): boolean {
  if (min && isDayKey(min) && key < min) return true;
  if (max && isDayKey(max) && key > max) return true;
  return false;
}

/** The month the calendar opens on: the field's value, else today (clamped into min/max). */
export function startMonth(value: string, today: string, min?: string | null, max?: string | null): string {
  if (isDayKey(value)) return value;
  if (max && isDayKey(max) && today > max) return max;
  if (min && isDayKey(min) && today < min) return min;
  return today;
}

export { monthCells };

/** Where the popover sits: below the field, flipped above when there is no room, kept inside the viewport. */
export function placePopover(
  anchor: { top: number; bottom: number; left: number; right: number },
  size: { width: number; height: number },
  viewport: { width: number; height: number },
  gap = 4,
): { top: number; left: number } {
  const below = anchor.bottom + gap + size.height <= viewport.height || anchor.top - gap - size.height < 0;
  const top = below ? anchor.bottom + gap : anchor.top - gap - size.height;
  let left = anchor.left;
  if (left + size.width > viewport.width - 8) left = anchor.right - size.width;
  left = Math.max(8, Math.min(left, viewport.width - size.width - 8));
  return { top: Math.max(8, top), left };
}

// ---- P-57c (D-232 sec.4 / QX-611): date + time (`datetime-local`) ----

/** `YYYY-MM-DDTHH:mm` (seconds / millis tolerated and cut). */
export function isDateTimeValue(v: string | null | undefined): boolean {
  return !!v && /^\d{4}-\d{2}-\d{2}T([01]\d|2[0-3]):[0-5]\d/.test(v) && isDayKey(v.slice(0, 10));
}

/** The day part of a datetime-local value / min / max ('' when it has none). Day-only values pass through. */
export function dayOf(v: string | null | undefined): string {
  const d = (v ?? '').slice(0, 10);
  return isDayKey(d) ? d : '';
}

/** The `HH:mm` part of a datetime-local value ('' when it has none). */
export function timeOf(v: string | null | undefined): string {
  return isDateTimeValue(v) ? (v as string).slice(11, 16) : '';
}

export function joinDateTime(day: string, time: string): string {
  return `${day}T${time}`;
}

/** Keep a datetime-local value inside the field's own min / max (both optional, same format). */
export function clampDateTime(value: string, min?: string | null, max?: string | null): string {
  let v = value;
  if (max && isDateTimeValue(max) && v > max.slice(0, 16)) v = max.slice(0, 16);
  if (min && isDateTimeValue(min) && v < min.slice(0, 16)) v = min.slice(0, 16);
  return v;
}

/** dd/mm/yyyy HH:mm for a datetime-local value ('' for anything else) - the display format of date+time fields. */
export function formatDmyHm(v: string | null | undefined): string {
  if (!isDateTimeValue(v)) return '';
  return `${formatDmy((v as string).slice(0, 10))} ${(v as string).slice(11, 16)}`;
}

/** The HH:mm a freshly picked day gets: the value's own time, else the current time (rounded down to 5 minutes). */
export function defaultTime(current: string | null | undefined, now: Date): string {
  const t = timeOf(current);
  if (t) return t;
  const m = Math.floor(now.getMinutes() / 5) * 5;
  return `${String(now.getHours()).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
